import { Vector2 } from "../../common/vector2.js";
import { NineSliceDictionary } from "../../image_loader/nine_slicer/nine_slice_dictionary.js";
import { BasicEventsController } from "../../touch_events_controller/basic_events_controller.js";
import { AbstractTooltip } from "./abstract_tooltip.js";
import { getDomHost } from "../dom_backend/dom_backend.js";
import { isNullCanvasContext } from "../dom_backend/null_canvas_context.js";

export class TooltipManager
{
  public static ctx: CanvasRenderingContext2D;
  public static ns_dictionary = NineSliceDictionary.values;

  private static tooltip_stack: AbstractTooltip[] = [];
  private static canvas: HTMLCanvasElement;
  private static events_controller: BasicEventsController;
  private static is_down_outside_of_tooltip: boolean = false;
  private static width:number;
  private static height:number;
  private static replay_background_tap: ( ( p: Vector2 ) => void ) | null = null;
  private static dom_host: HTMLDivElement | null = null;

  public static init(
    ctx: CanvasRenderingContext2D,
    width:number,
    height:number,
    replay_background_tap?: ( p: Vector2 ) => void
  ): void
  {
    this.ctx = ctx;
    this.canvas = ctx.canvas;
    this.width = width;
    this.height = height;
    this.dom_host = isNullCanvasContext( ctx ) ? getDomHost( this.canvas ) : null;
    this.replay_background_tap = replay_background_tap ?? null;

    this.events_controller = new BasicEventsController( this.canvas );
    this.events_controller.init(
      ( p:Vector2 ) => this.handleDown( p ),
      ( p:Vector2 ) => this.handleUp  ( p ),
      ( p:Vector2 ) => this.handleMove( p ),
      false,
      true
    );
  }

  public static update( now: number ): void
  {
    if ( this.tooltip_stack.length == 0 )
      return;

    const top_tooltip = this.topTooltip();
    top_tooltip.update( now );
  }

  public static render(): void
  {
    if ( this.tooltip_stack.length == 0 )
      return;

    const top_tooltip = this.topTooltip();
    if ( !top_tooltip.shouldBeRendered() )
      return;

    if ( this.dom_host )
    {
      for ( const tooltip of this.tooltip_stack )
      {
        if ( !tooltip.isDomMode() )
          continue;
        tooltip.mountDomHost( this.canvas );
        const display = tooltip === top_tooltip ? "" : "none";
        const node = tooltip.getDomNode();
        if ( node.style.display !== display )
          node.style.display = display;
      }
      top_tooltip.render();
      return;
    }

    this.ctx.clearRect( 0, 0, this.width, this.height );
    top_tooltip.render();
  }

  private static handleDown( p:Vector2 ): void
  {
    if ( this.tooltip_stack.length === 0 )
      return;

    const inside = this.isInsideOpenedTooltip( p );
    if ( inside )
    {
      const top_tooltip = this.topTooltip();
      top_tooltip.onTouchStart( p, performance.now() );
      return;
    }

    this.is_down_outside_of_tooltip = true;
  }

  private static handleUp( p:Vector2 ): void
  {
    if ( this.tooltip_stack.length === 0 )
      return;

    const inside = this.isInsideOpenedTooltip( p );
    if ( inside )
    {
      const top_tooltip = this.topTooltip();
      top_tooltip.onTouchEnd( p, performance.now() );
      return;
    }

    if ( !this.is_down_outside_of_tooltip )
      return;

    this.is_down_outside_of_tooltip = false;
    this.closeTooltip();

    // The tooltip canvas consumed the physical gesture. Once it is hidden,
    // replay that same tap on the active UI so another chest or control reacts
    // immediately instead of requiring a second tap.
    if ( !this.isAnyTooltipOpened() )
    {
      queueMicrotask( () =>
      {
        this.replay_background_tap?.( p );
      } );
    }
  }

  private static handleMove( p:Vector2 ): void
  {
    if ( this.tooltip_stack.length === 0 )
      return;

    if ( this.isInsideOpenedTooltip( p ) )
    {
      const top_tooltip = this.topTooltip();
      top_tooltip.onTouchMove( p, performance.now() );
      return;
    }
  }

  private static isInsideOpenedTooltip( p:Vector2 ): boolean
  {
    if ( this.tooltip_stack.length === 0 )
      return false;

    const top_tooltip = this.topTooltip();
    return top_tooltip.containsPointGlobal( p );
  }

  public static openTooltip( tooltip: AbstractTooltip ): void
  {
    this.tooltip_stack.push( tooltip );
    if ( this.dom_host && tooltip.isDomMode() )
      tooltip.mountDomHost( this.canvas );
    requestAnimationFrame(() => {
      if (this.tooltip_stack.length > 0) {
        this.show();
      }
    });
  }

  public static closeTooltip(): void
  {
    if ( !this.isAnyTooltipOpened() )
      return;

    const tooltip_to_close = this.tooltip_stack.pop();
    
    if ( tooltip_to_close && typeof (tooltip_to_close as any).cleanup === 'function' )
    {
      (tooltip_to_close as any).cleanup();
    }

    if ( !this.isAnyTooltipOpened() ) {
      this.ctx.clearRect( 0, 0, this.width, this.height );
      this.hide();
    }
  }

  public static isAnyTooltipOpened() { return this.tooltip_stack.length > 0; }

  private static topTooltip(): AbstractTooltip
  {
    if ( this.tooltip_stack.length == 0 )
      throw new Error( 'topTooltip(): No tooltips are open' );

    return this.tooltip_stack[this.tooltip_stack.length - 1];
  }

  private static canvas_anim: Animation | null = null;

  private static hide(): void
  {
    if ( this.canvas_anim )
    {
      this.canvas_anim.cancel();
      this.canvas_anim = null;
    }
    this.canvas.style.display = "none";
  }

  private static show(): void
  {
    this.canvas.style.display = "block";
    this.ctx.clearRect( 0, 0, this.width, this.height );

    if ( typeof this.canvas.animate !== "function"
      || localStorage.getItem( "perf_diag_dialog_canvas_anim" ) === "1" )
      return;

    this.canvas_anim?.cancel();
    const anim = ( this.dom_host ?? this.canvas ).animate(
      [ { opacity: 0 }, { opacity: 1 } ],
      { duration: 120, fill: "both" }
    );
    this.canvas_anim = anim;
    anim.onfinish = () =>
    {
      if ( this.canvas_anim === anim )
      {
        anim.cancel();
        this.canvas_anim = null;
      }
    };
  }
}
